import type { SeoResolveData } from '@/features/seo-listing/types';
import { applySeoResolveDocumentMeta } from '@/features/seo-listing/utils';
import { LISTING_RESTORE_HISTORY_KEY, LISTING_RESTORE_STORAGE_KEY } from '../consts';
import type { FilterState } from '../store';
import type { ListingRestoreFilters, ListingRestoreSnapshot } from '../types';
import {
  shouldSkipListingRestoreForAnchor,
  shouldSkipListingRestoreForCatalogDestination,
} from './should-skip-listing-restore-for-anchor';

const isSeoResolveData = (value: unknown): value is SeoResolveData => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const seoPage = value as SeoResolveData;
  return (
    typeof seoPage.pageKey === 'string' &&
    typeof seoPage.pageType === 'string' &&
    typeof seoPage.pageName === 'string' &&
    typeof seoPage.permanentUrl === 'string' &&
    typeof seoPage.h1 === 'string' &&
    typeof seoPage.title === 'string'
  );
};

const isListingRestoreFilters = (value: unknown): value is ListingRestoreFilters => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const filters = value as ListingRestoreFilters;
  return (
    Array.isArray(filters.selectedFandoms) &&
    Array.isArray(filters.selectedTags) &&
    Array.isArray(filters.selectedUniverses) &&
    Array.isArray(filters.selectedStatuses) &&
    Array.isArray(filters.selectedRatings) &&
    Array.isArray(filters.selectedDirections) &&
    Array.isArray(filters.selectedCharacters) &&
    Array.isArray(filters.selectedPairingCharacters) &&
    typeof filters.searchQuery === 'string' &&
    typeof filters.sortBy === 'string' &&
    typeof filters.currentPage === 'number' &&
    typeof filters.activeTab === 'string'
  );
};

const isListingRestoreSnapshot = (value: unknown): value is ListingRestoreSnapshot => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const snapshot = value as ListingRestoreSnapshot;
  const hasValidSeoPage =
    !('seoPage' in snapshot) || snapshot.seoPage === null || isSeoResolveData(snapshot.seoPage);
  return (
    typeof snapshot.restoreId === 'string' &&
    snapshot.restoreId.length > 0 &&
    typeof snapshot.href === 'string' &&
    typeof snapshot.basePath === 'string' &&
    typeof snapshot.scrollY === 'number' &&
    (snapshot.seoPageTitle === null || typeof snapshot.seoPageTitle === 'string') &&
    hasValidSeoPage &&
    isListingRestoreFilters(snapshot.filters)
  );
};

export const mapFilterStateToRestoreFilters = (
  filterState: FilterState,
): ListingRestoreFilters => ({
  activeTab: filterState.activeTab,
  searchQuery: filterState.searchQuery,
  sortBy: filterState.sortBy,
  currentPage: filterState.currentPage,
  selectedFandoms: filterState.selectedFandoms,
  selectedTags: filterState.selectedTags,
  selectedUniverses: filterState.selectedUniverses,
  selectedStatuses: filterState.selectedStatuses,
  selectedRatings: filterState.selectedRatings,
  selectedDirections: filterState.selectedDirections,
  selectedCharacters: filterState.selectedCharacters,
  selectedPairingCharacters: filterState.selectedPairingCharacters,
});

export const createFilterStateFromRestoreSnapshot = (
  snapshot: ListingRestoreSnapshot,
): FilterState => ({
  ...snapshot.filters,
  isMobileFiltersOpen: false,
});

const readListingRestoreSnapshot = (): ListingRestoreSnapshot | null => {
  try {
    const rawValue = sessionStorage.getItem(LISTING_RESTORE_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue: unknown = JSON.parse(rawValue);
    return isListingRestoreSnapshot(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

export const clearListingRestoreSnapshot = (): void => {
  try {
    sessionStorage.removeItem(LISTING_RESTORE_STORAGE_KEY);
  } catch {
    // Private mode or disabled storage — ignore
  }
};

export const persistListingRestoreSnapshot = (
  basePath: string,
  filterState: FilterState,
  seoPageTitle: string | null,
  seoPage: SeoResolveData | null = null,
): void => {
  const restoreId = crypto.randomUUID();
  const href = `${window.location.pathname}${window.location.search}`;
  const snapshot: ListingRestoreSnapshot = {
    restoreId,
    href,
    basePath,
    filters: mapFilterStateToRestoreFilters(filterState),
    seoPageTitle,
    seoPage,
    scrollY: window.scrollY,
  };

  try {
    sessionStorage.setItem(LISTING_RESTORE_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    return;
  }

  window.history.replaceState(
    {
      ...window.history.state,
      [LISTING_RESTORE_HISTORY_KEY]: restoreId,
    },
    '',
    href,
  );
};

const isBackForwardNavigation = (): boolean => {
  const navigationEntry = performance.getEntriesByType('navigation')[0];
  return (
    navigationEntry instanceof PerformanceNavigationTiming &&
    navigationEntry.type === 'back_forward'
  );
};

export const normalizeListingRestoreSnapshot = (
  snapshot: ListingRestoreSnapshot,
): ListingRestoreSnapshot => ({
  ...snapshot,
  seoPage: snapshot.seoPage ?? null,
});

export const readMatchingListingRestoreSnapshot = (
  basePath: string,
): ListingRestoreSnapshot | null => {
  const snapshot = readListingRestoreSnapshot();
  if (!snapshot || snapshot.basePath !== basePath) {
    return null;
  }

  const restoreId = window.history.state?.[LISTING_RESTORE_HISTORY_KEY];
  if (typeof restoreId === 'string' && restoreId === snapshot.restoreId) {
    return normalizeListingRestoreSnapshot(snapshot);
  }

  const currentHref = `${window.location.pathname}${window.location.search}`;
  // Soft client-side Back keeps type "navigate" in PerformanceNavigationTiming.
  // Match the restored permalink href even without a back_forward navigation entry.
  if (currentHref === snapshot.href) {
    return normalizeListingRestoreSnapshot(snapshot);
  }

  // Full document reload on Back drops custom history.state; still restore
  // when the browser reports back_forward onto this listing/catalog path.
  if (
    isBackForwardNavigation() &&
    (currentHref === snapshot.href || window.location.pathname === basePath)
  ) {
    return normalizeListingRestoreSnapshot(snapshot);
  }

  return null;
};

export const restoreListingHref = (href: string): void => {
  const currentHref = `${window.location.pathname}${window.location.search}`;
  if (currentHref === href) {
    return;
  }

  window.history.replaceState(window.history.state, '', href);
};

export const isInternalNavigationAnchor = (
  anchor: HTMLAnchorElement,
  event: MouseEvent,
): boolean => {
  if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
    return false;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }

  let nextUrl: URL;
  try {
    nextUrl = new URL(anchor.href);
  } catch {
    return false;
  }

  if (nextUrl.origin !== window.location.origin) {
    return false;
  }

  const nextHref = `${nextUrl.pathname}${nextUrl.search}`;
  const currentHref = `${window.location.pathname}${window.location.search}`;
  return nextHref !== currentHref;
};

export const handleListingRestoreCaptureClick = (
  event: MouseEvent,
  basePath: string,
  filterState: FilterState,
  seoPageTitle: string | null,
  seoPage: SeoResolveData | null = null,
): void => {
  const eventTarget = event.target;
  if (!(eventTarget instanceof Element)) {
    return;
  }

  const anchor = eventTarget.closest('a');
  if (!(anchor instanceof HTMLAnchorElement)) {
    return;
  }

  if (!isInternalNavigationAnchor(anchor, event)) {
    return;
  }

  if (shouldSkipListingRestoreForAnchor(anchor.dataset)) {
    return;
  }

  let anchorUrl: URL;
  try {
    anchorUrl = new URL(anchor.href);
  } catch {
    return;
  }

  if (shouldSkipListingRestoreForCatalogDestination(anchorUrl.pathname)) {
    return;
  }

  persistListingRestoreSnapshot(basePath, filterState, seoPageTitle, seoPage);
};

export const readListingRestoreSnapshotOnClient = (
  basePath: string,
): ListingRestoreSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return readMatchingListingRestoreSnapshot(basePath);
};

export const applyListingRestoreChrome = (snapshot: ListingRestoreSnapshot): void => {
  restoreListingHref(snapshot.href);
  window.scrollTo(0, snapshot.scrollY);
  if (snapshot.seoPage) {
    applySeoResolveDocumentMeta(snapshot.seoPage);
  } else if (snapshot.seoPageTitle) {
    document.title = snapshot.seoPageTitle;
  }
  // Keep sessionStorage until SEO chrome is restored by the listing grid —
  // Soft Nav remounts may need to re-read the snapshot.
};
